import type { VariableMap } from '@apollo/client/core/LocalState';
import type { AxiosResponse } from 'axios';
import { JsonPointer } from 'json-ptr';
import { get, has, isEmpty, mapValues, overEvery, pick, stubTrue, tap } from 'lodash/fp';
import OpenAPIRequestValidator from 'openapi-request-validator';
import OpenAPIResponseValidator from 'openapi-response-validator';
import type { OpenAPI, OpenAPIV3 } from 'openapi-types';

import { httpMethods } from '@skippay/oas-to-gql-schema/dist/openApiToGraphQL/utils';

import { GqlOperationInfo, RequestMiddleware } from './gqlEngineUtils';
import { getObjectSelection } from './gqlObjectSelection';
import { BAD_REQUEST } from '../constants/httpStatusCodes';

type Validators = {
	requestValidator?: OpenAPIRequestValidator;
	responseValidator?: OpenAPIResponseValidator;
};

type ErrorShape = { errorCode: string; message?: string; path: string };

type RequestErrorShape = {
	errors: ErrorShape[];
	status: number;
};

type ResponseErrorsShape = {
	errors: ErrorShape[];
	message: string;
};

const isRequestError = (
	error: RequestErrorShape | ResponseErrorsShape
): error is RequestErrorShape => `status` in error;

const ignoreNullable =
	(data: any) =>
	(error: ErrorShape): boolean =>
		!(error.message?.startsWith(`should be`) && get(error.path, data) === null);

const arrayPathRe = /\[\d+]/;
const isNonArrayPathSegment = (segment: string): boolean => !arrayPathRe.test(segment);
const toPathSegmentsWithoutArray = (path: string): string[] =>
	path.split(`.`).filter(isNonArrayPathSegment);
/**
 * Ignores `required` error when the property is not selected with GqlSelection. Assumes the XFields is used, thus
 * BE omit sending fields, which are not requested.
 */
const ignoreRequiredNonSelected = (
	info: GqlOperationInfo,
	variables?: VariableMap
): ((error: ErrorShape) => boolean) => {
	const selectionObject = getObjectSelection(info, variables);
	return !selectionObject
		? stubTrue
		: (error) => {
				return !(
					error.errorCode === `required.openapi.responseValidation` &&
					!has(toPathSegmentsWithoutArray(error.path), selectionObject)
				);
		  };
};

export class OasValidator {
	private readonly cache = new Map<string, Validators>();
	private readonly operationsById: Record<string, OpenAPIV3.OperationObject>;

	constructor(private readonly document: OpenAPIV3.Document) {
		this.operationsById = Object.values(document.paths).reduce((acc, path) => {
			httpMethods.forEach((httpMethod) => {
				const operation = path![httpMethod];
				if (operation) {
					acc[operation.operationId!] = operation;
				}
			});
			return acc;
		}, {} as Record<string, OpenAPIV3.OperationObject>);

		this.enchanceAllResponsesWithUnauthorized();
	}

	// This add 401 response to all operations with bearerAuth
	private enchanceAllResponsesWithUnauthorized(): void {
		const unauthorizedResponse = this.document.components?.responses?.['401Unauthorized'];

		if (!unauthorizedResponse) return;

		Object.values(this.document.paths).forEach((path) => {
			if (!path) return;

			httpMethods.forEach((httpMethod) => {
				const operation: OpenAPI.Operation = path[httpMethod];
				if (operation?.security?.[0]?.bearerAuth && !operation.responses?.[401]) {
					operation.responses = {
						...operation.responses,
						401: unauthorizedResponse,
					};
				}
			});
		});
	}

	private dereference<Output>(item: Output | OpenAPIV3.ReferenceObject): Output {
		return `$ref` in item
			? this.dereference<Output>(
					JsonPointer.get(this.document, (item as OpenAPIV3.ReferenceObject).$ref) as
						| Output
						| OpenAPIV3.ReferenceObject
			  )
			: item;
	}

	getValidators(operationId: string): Validators {
		const cached = this.cache.get(operationId);
		if (cached) return cached;
		const operation = this.operationsById[operationId];
		let requestValidator: OpenAPIRequestValidator | undefined;
		const params = pick([`parameters`, `requestBody`], operation);
		if (!isEmpty(params)) {
			requestValidator = new OpenAPIRequestValidator({
				...(params as any),
				schemas: this.document.components?.schemas,
			});
		}
		let responseValidator: OpenAPIResponseValidator | undefined;
		if (!isEmpty(operation.responses)) {
			responseValidator = new OpenAPIResponseValidator({
				components: this.document.components,
				responses: mapValues(
					(response: OpenAPIV3.ResponsesObject | OpenAPIV3.ReferenceObject) =>
						this.dereference<OpenAPIV3.ResponsesObject>(response),
					operation.responses
				) as any,
			});
		}

		const validators: Validators = { requestValidator, responseValidator };
		this.cache.set(operationId, validators);
		return validators;
	}

	validateRequest(operationId: string, request: OpenAPI.Request): RequestErrorShape | undefined {
		const { requestValidator } = this.getValidators(operationId);
		return (
			requestValidator &&
			requestValidator.validateRequest({
				...request,
				headers: { ...request.headers, 'content-type': 'application/json' },
			})
		);
	}

	validateResponse(
		operationId: string,
		status: number,
		response: any,
		info?: GqlOperationInfo,
		variables?: VariableMap
	): ResponseErrorsShape | undefined {
		const { responseValidator } = this.getValidators(operationId);
		const error = responseValidator && responseValidator.validateResponse(status, response);
		if (error) {
			const errors = error.errors.filter(
				overEvery(
					[
						ignoreNullable(response),
						info && (ignoreRequiredNonSelected(info, variables) as any),
					].filter(Boolean)
				)
			);
			if (!isEmpty(errors)) {
				return { ...error, errors };
			}
		}
		return undefined;
	}
}

const reorderErrorPropertiesForBetterConsoleOutput: (error: ErrorShape) => ErrorShape = ({
	errorCode,
	message,
	path,
}) => ({ path, message, errorCode });

function reportOpenApiSchemaViolation(
	operationId,
	validationResult?: RequestErrorShape | ResponseErrorsShape
) {
	if (validationResult) {
		const requestError = isRequestError(validationResult);
		// eslint-disable-next-line no-console
		console.group(
			`❌ JSONSchema validation failed for ${
				requestError ? 'REQUEST' : 'RESPONSE'
			} operationId: ${operationId}`
		);
		// eslint-disable-next-line no-console
		console.table(validationResult.errors.map(reorderErrorPropertiesForBetterConsoleOutput));
		// eslint-disable-next-line no-console
		console.log(
			`Above error indicates either invalid response from the BE or, less probably, invalid OpenAPI specification.`
		);
		// eslint-disable-next-line no-console
		console.groupEnd();
	}
}

export const oasValidationMiddleware: (document: OpenAPIV3.Document) => RequestMiddleware = (
	document
) => {
	const validator = new OasValidator(document);
	return (next) => (request) => {
		const { operationId, info } = request;
		const requestErrors = validator.validateRequest(operationId, {
			body: request.groupedVariables.body,
			headers: request.groupedVariables.header,
			query: request.groupedVariables.query,
		});
		if (requestErrors) {
			reportOpenApiSchemaViolation(operationId, requestErrors);
		}
		return next(request).then(
			tap((axiosResponse?: AxiosResponse) => {
				const responseErrors = validator.validateResponse(
					operationId,
					axiosResponse?.status || BAD_REQUEST,
					axiosResponse?.data || {},
					info
				);

				if (responseErrors) {
					reportOpenApiSchemaViolation(operationId, responseErrors);
				}
			})
		);
	};
};
